import {CdEpTask} from '../../../interfaces/care/cd-execution-proof/cd-ep-task';
import {CdEpSkipReason} from '../../../interfaces/care/cd-execution-proof/cd-ep-skip-reason';
import {CdTemporaryMeasure} from '../../../interfaces/care/cd-execution-proof/cd-temporary-measure';
import {LightUser} from '../../../interfaces/core/light-user';
import {CdCarePlanItem} from '../../../interfaces/care/cd-care-plan/cd-care-plan-item';
import {DateTime} from 'luxon';
import {UserService} from '../../../services/core/user.service';
import {LightUserFactory} from '../../core/light-user-factory';
import {CdCarePlanItemFactory} from '../cd-care-plan/cd-care-plan-item-factory';
import {CdTemporaryMeasureFactory} from './cd-temporary-measure-factory';
import {CdEpSkipReasonFactory} from './cd-ep-skip-reason-factory';
import { v4 as uuidv4 } from 'uuid';


export class CdEpTaskFactory {
  /**
   *
   * @param cdEpTask
   */
  public static fromObject(cdEpTask: any): CdEpTask {
    return new class implements CdEpTask {
      id: number = cdEpTask.id;
      uuid: string = cdEpTask.uuid;
      cdExecutionProofId: number = cdEpTask.c_execution_proof_id;
      cdExecutionProofUuid: string = cdEpTask.c_execution_proof_uuid ?? null;
      cdCarePlanItemId: number = cdEpTask.c_care_plan_item_id;
      cdCarePlanItem: CdCarePlanItem = cdEpTask.c_care_plan_item
        ? CdCarePlanItemFactory.fromObject(cdEpTask.c_care_plan_item)
        : null;
      cdTemporaryMeasureId: number = cdEpTask.c_temporary_measure_id;
      cdTemporaryMeasureUuid: string = cdEpTask.c_temporary_measure_uuid
        ?? cdEpTask.c_temporary_measure?.uuid
        ?? null;
      cdTemporaryMeasure: CdTemporaryMeasure = cdEpTask.c_temporary_measure
        ? CdTemporaryMeasureFactory.fromObject(cdEpTask.c_temporary_measure)
        : null;
      isDone: boolean = !!cdEpTask.is_done;
      isNotToDo: boolean = false;
      isSkipped: boolean = !!cdEpTask.c_ep_skip_reason_id || !!cdEpTask.own_reason;
      cdEpSkipReasonId: number = cdEpTask.c_ep_skip_reason_id ?? null;
      cdEpSkipReason: CdEpSkipReason = cdEpTask.c_ep_skip_reason
        ? CdEpSkipReasonFactory.fromObject(cdEpTask.c_ep_skip_reason)
        : null;
      ownReason: string = cdEpTask.own_reason ?? null;
      comment: string = cdEpTask.comment ?? null;
      userId: number = cdEpTask.user_id;
      user: LightUser = cdEpTask.user ? LightUserFactory.fromObject(cdEpTask.user) : null;
      created: string = cdEpTask.created;
      modified: string = cdEpTask.modified;
    };
  }

  /**
   *
   * @param cdEpTasks
   */
  public static fromArray(cdEpTasks: any[]): CdEpTask[] {
    return cdEpTasks.map(cdEpTask => this.fromObject(cdEpTask));
  }

  /**
   *
   * @param cdCarePlanItem
   */
  public static fromCdCarePlanItem(cdCarePlanItem: CdCarePlanItem): CdEpTask {
    return new class implements CdEpTask {
      cdCarePlanItemId: number = cdCarePlanItem.id;
      cdCarePlanItem: CdCarePlanItem = cdCarePlanItem;
      cdEpSkipReason: CdEpSkipReason = null;
      cdEpSkipReasonId: number = null;
      cdExecutionProofId: number = null;
      cdExecutionProofUuid: string = null;
      cdTemporaryMeasure: CdTemporaryMeasure = null
      cdTemporaryMeasureId: number = null;
      cdTemporaryMeasureUuid: string = null;
      comment: string = null;
      isNotToDo: boolean = false;
      isSkipped: boolean = false;
      created: string = DateTime.local().toISO();
      id: number = null;
      isDone: boolean = false;
      modified: string = DateTime.local().toISO();
      ownReason: string = null;
      user: LightUser = LightUserFactory.fromUser(UserService.getUser());
      userId: number = UserService.getUser().id;
      uuid: string = uuidv4();
    };
  }

  /**
   *
   * @param cdCarePlanItems
   */
  public static fromCdCarePlanItems(cdCarePlanItems: CdCarePlanItem[]): CdEpTask[] {
    return cdCarePlanItems.map(this.fromCdCarePlanItem);
  }

  /**
   *
   * @param cdTemporaryMeasure
   */
  /**
   *
   * @param cdEpTask
   */
  public static toRemote(cdEpTask: CdEpTask): any {
    return {
      id: cdEpTask.id,
      uuid: cdEpTask.uuid,
      created: cdEpTask.created,
      modified: cdEpTask.modified,
      c_execution_proof_id: cdEpTask.cdExecutionProofId,
      c_execution_proof_uuid: cdEpTask.cdExecutionProofUuid,
      c_care_plan_item_id: cdEpTask.cdCarePlanItemId,
      c_temporary_measure_id: cdEpTask.cdTemporaryMeasureId,
      // Temporary measures are created on the client and only get a
      // backend id after sync. The uuid is set immediately, so the
      // backend uses it to resolve the relation.
      c_temporary_measure_uuid: cdEpTask.cdTemporaryMeasureUuid,
      is_done: cdEpTask.isDone,
      c_ep_skip_reason_id: cdEpTask.cdEpSkipReasonId,
      own_reason: cdEpTask.ownReason,
      comment: cdEpTask.comment,
      user_id: cdEpTask.userId
    };
  }

  /**
   *
   * @param cdEpTasks
   */
  public static toArray(cdEpTasks: CdEpTask[]): any[] {
    return cdEpTasks.map(this.toRemote);
  }

  /**
   *
   * @param cdTemporaryMeasure
   */
  public static fromCdTemporaryMeasure(cdTemporaryMeasure: CdTemporaryMeasure): CdEpTask {
    return new class implements CdEpTask {
      cdCarePlanItemId: number = null;
      cdCarePlanItem: CdCarePlanItem = null;
      cdEpSkipReason: CdEpSkipReason = null;
      cdEpSkipReasonId: number = null;
      cdExecutionProofId: number = null;
      cdExecutionProofUuid: string = null;
      cdTemporaryMeasure: CdTemporaryMeasure = cdTemporaryMeasure;
      cdTemporaryMeasureId: number = cdTemporaryMeasure.id;
      cdTemporaryMeasureUuid: string = cdTemporaryMeasure.uuid;
      comment: string = null;
      isNotToDo: boolean = false;
      isSkipped: boolean = false;
      created: string = DateTime.local().toISO();
      id: number = null;
      isDone: boolean = false;
      modified: string = DateTime.local().toISO();
      ownReason: string = null;
      user: LightUser = LightUserFactory.fromUser(UserService.getUser());
      userId: number = UserService.getUser().id;
      uuid: string = uuidv4();
    };
  }
}
